import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {ArrowLeft, Check, X, Calculator, TrendingUp, Filter} from 'lucide-react';

interface Pass {
  destination: string;
  passes: {
    name: string;
    price: number;
    duration: string;
    durationDays: number;
    includes: string[];
    excludes?: string[];
    recommended: boolean;
    category: string;
  }[];
}

const passData: Pass[] = [
  {
    destination: 'Tokyo',
    passes: [
      {
        name: 'Tokyo Wide Pass',
        price: 10180,
        duration: '3 consecutive days',
        durationDays: 3,
        category: 'Rail Pass',
        includes: ['All JR lines in Kanto', 'Narita Express', 'Shinkansen to Nikko', 'Mt. Fuji area trains'],
        excludes: ['Tokyo Metro/Toei lines', 'Private railways'],
        recommended: true
      },
      {
        name: 'Tokyo Subway Pass',
        price: 1500,
        duration: '72 hours',
        durationDays: 3,
        category: 'Transit Pass',
        includes: ['Tokyo Metro (9 lines)', 'Toei Subway (4 lines)', 'Unlimited rides'],
        excludes: ['JR lines', 'Private railways'],
        recommended: false
      },
      {
        name: 'Individual Tickets',
        price: 4000,
        duration: '3 days estimated',
        durationDays: 3,
        category: 'Pay Per Ride',
        includes: ['Pay per ride flexibility', 'Works on all lines'],
        excludes: [],
        recommended: false
      }
    ]
  },
  {
    destination: 'Singapore',
    passes: [
      {
        name: 'Singapore Tourist Pass',
        price: 30,
        duration: '3 days',
        durationDays: 3,
        category: 'Transit Pass',
        includes: ['Unlimited MRT/LRT', 'Unlimited bus', 'Airport transfer included'],
        excludes: [],
        recommended: true
      },
      {
        name: 'EZ-Link Card (Pay per ride)',
        price: 25,
        duration: 'No expiry',
        durationDays: 3,
        category: 'Pay Per Ride',
        includes: ['MRT/LRT/Bus', 'Convenience stores', 'Refundable deposit'],
        excludes: [],
        recommended: false
      }
    ]
  },
  {
    destination: 'Orlando Theme Parks',
    passes: [
      {
        name: 'Universal 3-Park Explorer',
        price: 349,
        duration: '14 days',
        durationDays: 14,
        category: 'Theme Park Pass',
        includes: ['Universal Studios', 'Islands of Adventure', 'Volcano Bay', 'Park-to-park access'],
        excludes: ['Express Pass', 'Parking'],
        recommended: true
      },
      {
        name: 'Disney 4-Day Park Hopper',
        price: 505,
        duration: '4 days',
        durationDays: 4,
        category: 'Theme Park Pass',
        includes: ['Magic Kingdom', 'EPCOT', 'Hollywood Studios', 'Animal Kingdom'],
        excludes: ['Water parks', 'Genie+'],
        recommended: false
      },
      {
        name: 'Individual Day Tickets',
        price: 600,
        duration: 'Per day',
        durationDays: 1,
        category: 'Single Day',
        includes: ['One park per day', 'Flexible dates'],
        excludes: ['Park hopping'],
        recommended: false
      }
    ]
  }
];

export default function PassComparisons() {
  const [selectedDestination, setSelectedDestination] = useState(passData[0].destination);
  const [showCalculator, setShowCalculator] = useState(false);
  const [tripDays, setTripDays] = useState(3);
  const [dailyRides, setDailyRides] = useState(4);
  const [filterCategory, setFilterCategory] = useState<string>('All');
  const [sortBy, setSortBy] = useState<'price' | 'value' | 'duration'>('value');

  const currentDestination = passData.find(p => p.destination === selectedDestination) || passData[0];

  // Get currency symbol
  const getCurrency = () => {
    if (currentDestination.destination === 'Tokyo') return '¥';
    if (currentDestination.destination === 'Singapore') return 'S$';
    return '$';
  };

  // Calculate value score (price per day)
  const calculateValueScore = (pass: any) => {
    return pass.price / pass.durationDays;
  };

  // Get unique categories
  const categories = ['All', ...Array.from(new Set(currentDestination.passes.map(p => p.category)))];

  // Filter and sort passes
  const filteredAndSortedPasses = useMemo(() => {
    let filtered = currentDestination.passes.filter(pass => {
      if (filterCategory !== 'All' && pass.category !== filterCategory) return false;
      return true;
    });

    return filtered.sort((a, b) => {
      switch (sortBy) {
        case 'price':
          return a.price - b.price;
        case 'value':
          return calculateValueScore(a) - calculateValueScore(b);
        case 'duration':
          return b.durationDays - a.durationDays;
        default:
          return 0;
      }
    });
  }, [currentDestination, filterCategory, sortBy]);

  // Calculate estimated individual ticket cost
  const estimatedIndividualCost = useMemo(() => {
    const avgFareMap: { [key: string]: number } = {
      'Tokyo': 240,
      'Singapore': 2,
      'Orlando Theme Parks': 120
    };
    const avgFare = avgFareMap[currentDestination.destination] || 5;
    return tripDays * dailyRides * avgFare;
  }, [currentDestination.destination, tripDays, dailyRides]);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#4A90E2] to-[#5aa0f2] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/deals" className="inline-flex items-center text-gray-100 hover:text-white mb-4 transition-colors">
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Deals
          </Link>
          <h1 className="text-5xl font-bold mb-4">Pass Comparisons</h1>
          <p className="text-xl text-gray-100">Find out which pass gives you the best value</p>
        </div>
      </div>

      {/* Destination Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-wrap gap-3 mb-6">
          {passData.map((pass) => (
            <button
              key={pass.destination}
              onClick={() => setSelectedDestination(pass.destination)}
              className={`px-6 py-2 rounded-full font-medium transition-all ${
                selectedDestination === pass.destination
                  ? 'bg-[#4A90E2] text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              {pass.destination}
            </button>
          ))}
        </div>

        {/* Filters & Controls */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <button
            onClick={() => setShowCalculator(!showCalculator)}
            className="flex items-center justify-center gap-2 px-6 py-3 bg-[#F7B32B] text-white rounded-lg hover:bg-[#e5a325] transition-colors font-medium shadow-md"
          >
            <Calculator className="w-5 h-5" />
            Value Calculator
          </button>

          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-6 py-3 bg-white rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#4A90E2] focus:border-transparent font-medium"
          >
            <option value="All">All Categories</option>
            {categories.slice(1).map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
            className="px-6 py-3 bg-white rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#4A90E2] focus:border-transparent font-medium"
          >
            <option value="value">Best Value</option>
            <option value="price">Lowest Price</option>
            <option value="duration">Longest Duration</option>
          </select>
        </div>

        {/* Value Calculator */}
        {showCalculator && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <h3 className="text-xl font-bold text-[#0A2540] mb-4 flex items-center">
              <Calculator className="w-6 h-6 mr-2 text-[#F7B32B]" />
              Pass Value Calculator
            </h3>
            
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Trip Duration (days)
                </label>
                <input
                  type="number"
                  min="1"
                  max="30"
                  value={tripDays}
                  onChange={(e) => setTripDays(parseInt(e.target.value) || 1)}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#4A90E2] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Daily Rides (estimated)
                </label>
                <input
                  type="number"
                  min="1"
                  max="20"
                  value={dailyRides}
                  onChange={(e) => setDailyRides(parseInt(e.target.value) || 1)}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#4A90E2] focus:border-transparent"
                />
              </div>
            </div>

            <div className="bg-gradient-to-r from-[#4A90E2] to-[#5aa0f2] text-white rounded-lg p-4">
              <div className="text-sm mb-1">Estimated cost without pass:</div>
              <div className="text-3xl font-bold">
                {getCurrency()}{estimatedIndividualCost.toFixed(0)}
              </div>
              <div className="text-sm mt-2 opacity-90">
                Based on {tripDays} days × {dailyRides} rides/day
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Pass Comparison */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="grid md:grid-cols-3 gap-6">
          {filteredAndSortedPasses.map((pass, index) => {
            const valueScore = calculateValueScore(pass);
            const savings = estimatedIndividualCost - pass.price;
            const isWorthIt = savings > 0;

            return (
              <div
                key={index}
                className={`relative bg-white rounded-xl shadow-lg overflow-hidden transition-all hover:shadow-2xl ${
                  pass.recommended ? 'ring-4 ring-[#F7B32B] transform scale-105' : ''
                }`}
              >
                {pass.recommended && (
                  <div className="bg-[#F7B32B] text-white text-center py-2 font-bold">
                    RECOMMENDED
                  </div>
                )}

                <div className="p-6">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-2xl font-bold text-[#0A2540]">{pass.name}</h3>
                  </div>
                  
                  <div className="mb-2">
                    <span className="inline-block px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-semibold">
                      {pass.category}
                    </span>
                  </div>

                  <div className="text-4xl font-bold text-[#FF6B35] mb-2">
                    {getCurrency()}{pass.price}
                  </div>
                  <p className="text-gray-600 mb-2">{pass.duration}</p>

                  {/* Value Metrics */}
                  <div className="bg-gray-50 rounded-lg p-3 mb-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Price per day:</span>
                      <span className="font-bold text-[#0A2540]">
                        {getCurrency()}{valueScore.toFixed(0)}
                      </span>
                    </div>
                    {showCalculator && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">vs Individual:</span>
                        <span className={`font-bold ${isWorthIt ? 'text-green-600' : 'text-red-600'}`}>
                          {isWorthIt ? `Save ${getCurrency()}${Math.abs(savings).toFixed(0)}` : `Pay ${getCurrency()}${Math.abs(savings).toFixed(0)} more`}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-[#0A2540] mb-3">Includes:</h4>
                      <ul className="space-y-2">
                        {pass.includes.map((item, i) => (
                          <li key={i} className="flex items-start text-sm">
                            <Check className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" />
                            <span className="text-gray-700">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {pass.excludes && pass.excludes.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-[#0A2540] mb-3">Excludes:</h4>
                        <ul className="space-y-2">
                          {pass.excludes.map((item, i) => (
                            <li key={i} className="flex items-start text-sm">
                              <X className="w-5 h-5 text-red-500 mr-2 flex-shrink-0" />
                              <span className="text-gray-700">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  <button
                    className={`w-full mt-6 py-3 rounded-lg font-semibold transition-colors ${
                      pass.recommended
                        ? 'bg-[#F7B32B] text-white hover:bg-[#e5a325]'
                        : 'bg-[#4A90E2] text-white hover:bg-[#3a80d2]'
                    }`}
                  >
                    Learn More
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Value Insights */}
        {showCalculator && (
          <div className="mt-8 bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-6 border-2 border-green-200">
            <h3 className="text-xl font-bold text-[#0A2540] mb-4 flex items-center">
              <TrendingUp className="w-6 h-6 mr-2 text-green-600" />
              Value Insights
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-white rounded-lg p-4">
                <div className="text-sm text-gray-600 mb-1">Best Value Pass</div>
                <div className="text-xl font-bold text-[#0A2540]">
                  {filteredAndSortedPasses.sort((a, b) => calculateValueScore(a) - calculateValueScore(b))[0]?.name}
                </div>
                <div className="text-sm text-green-600 font-semibold mt-1">
                  {getCurrency()}{calculateValueScore(filteredAndSortedPasses[0]).toFixed(0)}/day
                </div>
              </div>
              <div className="bg-white rounded-lg p-4">
                <div className="text-sm text-gray-600 mb-1">Maximum Savings</div>
                <div className="text-xl font-bold text-[#0A2540]">
                  {getCurrency()}{Math.max(...filteredAndSortedPasses.map(p => estimatedIndividualCost - p.price)).toFixed(0)}
                </div>
                <div className="text-sm text-blue-600 font-semibold mt-1">
                  vs individual tickets
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}